import React, { useState } from 'react';
import { mk, getAllBills } from '../utils';

const f0 = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
const fc = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getSpendColor(amt, max) {
  if (!amt || !max) return null; // no spending — no background tint
  const intensity = Math.min(1, amt / max);
  if (intensity < 0.25) return 'rgba(107,203,119,.22)';
  if (intensity < 0.5)  return 'rgba(107,203,119,.50)';
  if (intensity < 0.75) return 'rgba(255,217,61,.50)';
  return intensity < 0.9 ? 'rgba(255,107,107,.50)' : 'rgba(255,107,107,.85)';
}

export default function SpendingCalendar({ state }) {
  const now = new Date();
  const [year, setYear]     = useState(now.getFullYear());
  const [month, setMonth]   = useState(now.getMonth());
  const [selected, setSelected] = useState(null);

  // ── Data ────────────────────────────────────────────────────────────────────
  const moKey      = mk(year, month);
  const varSpend   = state.varSpend?.[moKey] || {};
  const allBills   = getAllBills(state.cards || [], state.fixedBills || [], state.subscriptions || []);
  const paidBills  = (state.paidBills || {})[`${year}-${month}`] || {};

  // Day → total variable spending
  const dayTotals = {};
  Object.values(varSpend).forEach((entries) => {
    (Array.isArray(entries) ? entries : []).forEach((e) => {
      let d = 0;
      if (e.date) {
        d = parseInt(e.date.slice(8, 10), 10);
      } else if (e.ts) {
        const dt = new Date(e.ts);
        if (dt.getFullYear() === year && dt.getMonth() === month) d = dt.getDate();
      }
      if (d) dayTotals[d] = (dayTotals[d] || 0) + (e.amt || 0);
    });
  });

  // Day → bills due (clamped to days in this month)
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const billsByDay  = {};
  allBills.forEach((b) => {
    const d = Math.min(b.due || 1, daysInMonth);
    (billsByDay[d] = billsByDay[d] || []).push(b);
  });

  const maxDay      = Math.max(0, ...Object.values(dayTotals));
  const totalMonth  = Object.values(dayTotals).reduce((s, v) => s + v, 0);
  const totalBills  = allBills.reduce((s, b) => s + b.amt, 0);
  const firstDow    = new Date(year, month, 1).getDay();

  // ── Selected day data ────────────────────────────────────────────────────────
  const selectedSpend = selected
    ? Object.entries(varSpend).flatMap(([cat, entries]) =>
        (Array.isArray(entries) ? entries : [])
          .filter((e) => {
            if (e.date) return parseInt(e.date.slice(8, 10), 10) === selected;
            if (e.ts) {
              const dt = new Date(e.ts);
              return dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === selected;
            }
            return false;
          })
          .map((e) => ({ ...e, cat }))
      )
    : [];

  const selectedBills = selected ? (billsByDay[selected] || []) : [];

  // ── Navigation ───────────────────────────────────────────────────────────────
  const prevMonth = () => {
    if (month === 0) { setYear((y) => y - 1); setMonth(11); }
    else setMonth((m) => m - 1);
    setSelected(null);
  };
  const nextMonth = () => {
    if (month === 11) { setYear((y) => y + 1); setMonth(0); }
    else setMonth((m) => m + 1);
    setSelected(null);
  };
  const isToday = (d) =>
    d === now.getDate() && month === now.getMonth() && year === now.getFullYear();

  const monthName = new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: 'var(--txt)', cursor: 'pointer', fontSize: 18, padding: '4px 10px' }}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: 15 }}>{monthName}</div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 2, display: 'flex', gap: 12, justifyContent: 'center' }}>
            <span>🛒 <strong style={{ color: 'var(--txt)' }}>{f0(totalMonth)}</strong> spent</span>
            {totalBills > 0 && <span>📋 <strong style={{ color: 'var(--txt)' }}>{f0(totalBills)}</strong> in bills</span>}
          </div>
        </div>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: 'var(--txt)', cursor: 'pointer', fontSize: 18, padding: '4px 10px' }}>›</button>
      </div>

      {/* ── Day-of-week headers ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 3 }}>
        {DAYS.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--mut)', padding: '2px 0' }}>{d}</div>
        ))}
      </div>

      {/* ── Calendar grid ──────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 12 }}>
        {Array.from({ length: firstDow }).map((_, i) => <div key={'e' + i} />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => {
          const amt        = dayTotals[d] || 0;
          const bills      = billsByDay[d] || [];
          const isSelected = selected === d;
          const spendBg    = getSpendColor(amt, maxDay);
          const hasBills   = bills.length > 0;

          return (
            <div
              key={d}
              onClick={() => setSelected(isSelected ? null : d)}
              style={{
                minHeight: 44,
                borderRadius: 8,
                cursor: 'pointer',
                background: isSelected ? 'var(--ylw)' : (spendBg || (hasBills ? 'var(--surf2)' : 'transparent')),
                border: isToday(d)
                  ? '2px solid var(--ylw)'
                  : isSelected
                    ? 'none'
                    : hasBills
                      ? '1px solid var(--bdr)'
                      : '1px solid transparent',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                padding: '4px 2px 3px',
                gap: 2,
                transition: 'transform .1s',
                transform: isSelected ? 'scale(1.06)' : 'scale(1)',
              }}
            >
              {/* Day number */}
              <div style={{
                fontSize: 11,
                fontWeight: isToday(d) ? 900 : hasBills ? 700 : 500,
                color: isSelected ? '#000' : isToday(d) ? 'var(--ylw)' : 'var(--txt)',
                lineHeight: 1.2,
              }}>
                {d}
              </div>

              {/* Variable spending amount */}
              {amt > 0 && (
                <div style={{ fontSize: 8, fontWeight: 700, color: isSelected ? '#000' : 'var(--mut)', lineHeight: 1 }}>
                  {f0(amt).replace('$', '')}
                </div>
              )}

              {/* Bill dots */}
              {hasBills && (
                <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', marginTop: 'auto', paddingBottom: 1 }}>
                  {bills.slice(0, 4).map((b) => (
                    <div
                      key={b.id}
                      title={`${b.name} — ${fc(b.amt)}`}
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: '50%',
                        background: isSelected ? 'rgba(0,0,0,.5)' : (paidBills[b.id] ? 'var(--grn)' : b.col),
                        opacity: paidBills[b.id] ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                  {bills.length > 4 && (
                    <span style={{ fontSize: 7, color: isSelected ? '#000' : 'var(--mut)', fontWeight: 700 }}>+{bills.length - 4}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 700 }}>Spending:</span>
        {[['Low', 'rgba(107,203,119,.50)'], ['Mid', 'rgba(255,217,61,.50)'], ['High', 'rgba(255,107,107,.85)']].map(([l, c]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--mut)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: c, display: 'inline-block' }} />{l}
          </span>
        ))}
        <span style={{ fontSize: 10, color: 'var(--mut)', fontWeight: 700, marginLeft: 4 }}>Bills:</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--mut)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />unpaid
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--mut)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--grn)', display: 'inline-block', opacity: 0.6 }} />paid
        </span>
      </div>

      {/* ── Selected day detail ─────────────────────────────────────────────── */}
      {selected && (
        <div className="card cp">
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 12 }}>
            {new Date(year, month, selected).toLocaleDateString('default', { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>

          {/* Bills due this day */}
          {selectedBills.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                📋 Bills Due
              </div>
              {selectedBills.map((b) => {
                const isPaid = !!paidBills[b.id];
                return (
                  <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)', opacity: isPaid ? 0.6 : 1 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: isPaid ? 'var(--grn)' : b.col, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, textDecoration: isPaid ? 'line-through' : 'none', color: isPaid ? 'var(--mut)' : 'var(--txt)' }}>
                        {b.name}
                      </div>
                      {b.isSub && <div style={{ fontSize: 10, color: 'var(--mut)' }}>subscription</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: isPaid ? 'var(--grn)' : 'var(--red)' }}>{fc(b.amt)}</div>
                      <div style={{ fontSize: 10, color: isPaid ? 'var(--grn)' : 'var(--mut)', fontWeight: 600 }}>{isPaid ? '✓ paid' : 'unpaid'}</div>
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 12, fontWeight: 700 }}>
                <span style={{ color: 'var(--mut)' }}>Bills total</span>
                <span style={{ color: 'var(--red)' }}>{fc(selectedBills.reduce((s, b) => s + b.amt, 0))}</span>
              </div>
            </>
          )}

          {/* Variable spending this day */}
          {selectedSpend.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8, marginTop: selectedBills.length > 0 ? 12 : 0 }}>
                🛒 Variable Spending
              </div>
              {selectedSpend.map((e, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--bdr)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{e.place || e.note || e.cat}</div>
                    <div style={{ fontSize: 10, color: 'var(--mut)', textTransform: 'capitalize' }}>{e.cat}</div>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--red)', flexShrink: 0 }}>{fc(e.amt)}</div>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: 12, fontWeight: 700 }}>
                <span style={{ color: 'var(--mut)' }}>Spending total</span>
                <span style={{ color: 'var(--red)' }}>{fc(selectedSpend.reduce((s, e) => s + e.amt, 0))}</span>
              </div>
            </>
          )}

          {/* Combined total when both exist */}
          {selectedBills.length > 0 && selectedSpend.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0 2px', fontSize: 13, fontWeight: 800, borderTop: '2px solid var(--bdr)', marginTop: 4 }}>
              <span>Total out this day</span>
              <span style={{ color: 'var(--red)' }}>
                {fc(selectedBills.reduce((s, b) => s + b.amt, 0) + selectedSpend.reduce((s, e) => s + e.amt, 0))}
              </span>
            </div>
          )}

          {/* Empty state */}
          {selectedBills.length === 0 && selectedSpend.length === 0 && (
            <div style={{ color: 'var(--mut)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
              Nothing logged for this day
            </div>
          )}
        </div>
      )}
    </div>
  );
}
